import type { PaymentMethods } from '@/components/form-elements/PaymentMethodSelection/types';
import type { SearchData } from '@/features/Global/types';
import type { PaymentPeriodKey } from '@/utils/formatters/paymentPeroid';
import { RequestKonfigurationsParameters } from '@/services/api/types';

export interface TariffFeatureItem {
  type: 'item';
  identifier: string;
  label: string;
}

export interface TariffFeatureGroup {
  type: 'group';
  label: string;
  items: TariffFeatureItem[];
}

export interface TariffDegreeOfFulfillment {
  wert: number;
  merkmale: {
    nichtErfuellt: TariffFeatureGroup[];
    teilweiseErfuellt: TariffFeatureGroup[];
    erfuellt: TariffFeatureGroup[];
  };
}

export interface TariffCompany {
  links: {
    logo: string;
  };
  name: string;
  id: string;
  tsId: string; // Tarifservice company id -> required to load company questions
  zahlart: PaymentMethods | never[];
}

export enum BASE_TARIFF_INFO_TYPE {
  WARNING = 'Warning',
  INFO = 'Info',
}
export interface BaseTariffAdditionalInfo {
  text: string;
  type: BASE_TARIFF_INFO_TYPE;
}

export interface TariffFilters {
  directAgreement: boolean;
}

export interface BaseTariff {
  deckungssumme: number;
  versicherungssumme?: number;
  selbstbeteiligung: number;
  betrag: number;
  zahlweise: PaymentPeriodKey;
  laufzeit: string;
  identifier: string;
  tariflink: string;
  name: string;
  gesellschaft: TariffCompany;
  demvRabattiert: boolean;
  hinweise: string[];
  erfuellungsgrad: TariffDegreeOfFulfillment;
  risikoDemver: boolean;
  zusatzInfo?: string;
  disableContract?: boolean;
  beschreibung?: string;
  zusatzInfos?: BaseTariffAdditionalInfo[];
  allgemeinerRabatt: boolean;
}

export interface TariffWithDocuments extends BaseTariff {
  dokumente: string[];
}

export interface TariffError {
  insuranceId: number;
  tarifId: string;
  gesellschaftsname: string;
  tarifname: string;
  fehlerbeschreibung: string[];
}

export interface TariffResponseNote {
  fachlicheFehler: string[];
  technischeFehler: string[];
  anbieter: string;
}

export interface TariffNoteMap {
  [key: string]: TariffResponseNote;
}

export interface TariffResponseErrorNote {
  fehler: string[];
  anbieter: string;
}

export interface TariffErrorNoteMap {
  [key: string]: TariffResponseErrorNote;
}

export interface TariffRequestResponse {
  allTimeTariffs: BaseTariff[];
  id: string;
  tarife: BaseTariff[];
  tariffehler: TariffError[];
  links: {
    log: string;
  };
  requestTime: number;
  hinweise: TariffResponseNote[];
  fehler: TariffResponseErrorNote[];
  nichtBerechneteTarife: unknown[];
  /**
   * Gesellschaften, die unter dieser berechnungsId bereits angefragt wurden, auch solche ohne
   * Tarif und ohne Tariffehler. Nach einem Deep-Link legt der Store dafür leere Chunks an,
   * damit der Direktvereinbarungs-Toggle sie nicht erneut anfragt (VGL-7196).
   */
  angefragteGesellschaften: number[];
}

export enum TARIFF_SEARCH_REQUEST_IDENTIFIER {
  KFZ_AENDERUNGSANTRAG = 'aenderungsantrag',
  ALL_TIME_TARIFF = 'allTimeTariff',
}

export interface TariffSearchRequestBody extends SearchData {
  konfigurationsParameter: RequestKonfigurationsParameters & {
    gesellschaftsfilter: number[];
    alternateTariffSearchRequestIdentifier?: TARIFF_SEARCH_REQUEST_IDENTIFIER;
  };
}

export interface TariffChunk {
  tariffs: BaseTariff[];
  tariffErrors: TariffError[];
}
